import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { PublicLayout } from './layouts/PublicLayout';
import { DashboardLayout } from './layouts/DashboardLayout';
import { RouteErrorPage } from './components/routing/RouteErrorPage';
import { IOSAppBanner } from './components/IOSAppBanner';

// Lazy Imports
import { lazyRetry as safeLazyRetry } from './utils/lazyRetry';

// Lazy Imports
const LandingPage = safeLazyRetry(() => import('./pages/LandingPage').then(module => ({ default: module.LandingPage })), 'LandingPage');
const CarRepairPage = safeLazyRetry(() => import('./features/automotive/pages/CarRepairPage').then(module => ({ default: module.CarRepairPage })), 'CarRepairPage');
const AdminDashboard = safeLazyRetry(() => import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard })), 'AdminDashboard');
const PrivacyPage = safeLazyRetry(() => import('./pages/PrivacyPage').then(module => ({ default: module.PrivacyPage })), 'PrivacyPage');
const TermsPage = safeLazyRetry(() => import('./pages/TermsPage').then(module => ({ default: module.TermsPage })), 'TermsPage');
const ContactPage = safeLazyRetry(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })), 'ContactPage');
const AboutPage = safeLazyRetry(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })), 'AboutPage');
const KvkkPage = safeLazyRetry(() => import('./pages/KvkkPage').then(module => ({ default: module.KvkkPage })), 'KvkkPage');
const DownloadPage = safeLazyRetry(() => import('./pages/DownloadPage').then(module => ({ default: module.DownloadPage })), 'DownloadPage');
const InstagramBridgePage = safeLazyRetry(() => import('./pages/InstagramBridgePage').then(module => ({ default: module.InstagramBridgePage })), 'InstagramBridgePage');
const AppCampaignRedirectPage = safeLazyRetry(() => import('./pages/AppCampaignRedirectPage').then(module => ({ default: module.AppCampaignRedirectPage })), 'AppCampaignRedirectPage');
const DemoPage = safeLazyRetry(() => import('./pages/DemoPage').then(module => ({ default: module.DemoPage })), 'DemoPage');

// Sanayi Raporu Pages
const SanayiRaporuPage = safeLazyRetry(() => import('./features/sanayiRaporu/pages/SanayiRaporuPage').then(module => ({ default: module.SanayiRaporuPage })), 'SanayiRaporuPage');
const RaporDetayPage = safeLazyRetry(() => import('./features/sanayiRaporu/pages/RaporDetayPage').then(module => ({ default: module.RaporDetayPage })), 'RaporDetayPage');
const SanayiRaporuPaketlerPage = safeLazyRetry(() => import('./features/sanayiRaporu/pages/SanayiRaporuPaketlerPage').then(module => ({ default: module.SanayiRaporuPaketlerPage })), 'SanayiRaporuPaketlerPage');

// Default imports
const PremiumPage = safeLazyRetry(() => import('./pages/PremiumPage'), 'PremiumPage');
const PremiumSuccessPage = safeLazyRetry(() => import('./pages/PremiumSuccessPage'), 'PremiumSuccessPage');
const SupportPage = safeLazyRetry(() => import('./pages/SupportPage'), 'SupportPage');

// Loading Component
const FullPageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<FullPageLoading />}>{children}</Suspense>
);

// Protected Route Wrapper
function ProtectedRoute({
  children,
  allowAnonymous = false,
}: {
  children: React.ReactNode;
  allowAnonymous?: boolean;
}) {
  const { user, loading } = useAuth();
  const [hasResolvedAuth, setHasResolvedAuth] = React.useState(() => !loading);

  React.useEffect(() => {
    if (!loading) {
      setHasResolvedAuth(true);
    }
  }, [loading]);

  if (!hasResolvedAuth) {
    return null; // Layout handles loading state usually, or we can return a spinner here
  }

  // Allow anonymous (or even unauthenticated) users only when explicitly allowed.
  if (!user) {
    return allowAnonymous ? <>{children}</> : <Navigate to="/" replace />;
  }

  if (!allowAnonymous && user.userType === 'anonymous') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        element: <LazyRoute><LandingPage /></LazyRoute>,
      },
      {
        path: 'privacy',
        element: <LazyRoute><PrivacyPage /></LazyRoute>,
      },
      {
        path: 'terms',
        element: <LazyRoute><TermsPage /></LazyRoute>,
      },
      {
        path: 'contact',
        element: <LazyRoute><ContactPage /></LazyRoute>,
      },
      {
        path: 'hakkimizda',
        element: <LazyRoute><AboutPage /></LazyRoute>,
      },
      {
        path: 'kvkk',
        element: <LazyRoute><KvkkPage /></LazyRoute>,
      },
      {
        path: 'premium',
        element: <LazyRoute><PremiumPage /></LazyRoute>,
      },
      {
        path: 'indir',
        element: <LazyRoute><DownloadPage /></LazyRoute>,
      },
      {
        path: 'premium/success',
        element: <LazyRoute><PremiumSuccessPage /></LazyRoute>,
      },
      {
        path: 'destek',
        element: <LazyRoute><SupportPage /></LazyRoute>,
      },
    ],
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute allowAnonymous>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: 'araba',
        element: <LazyRoute><CarRepairPage /></LazyRoute>,
      },
      {
        path: 'sanayi-raporu',
        element: <LazyRoute><SanayiRaporuPage /></LazyRoute>,
      },
      {
        path: 'sanayi-raporu/paketler',
        element: <LazyRoute><SanayiRaporuPaketlerPage /></LazyRoute>,
      },
      {
        path: 'rapor/:raporId',
        element: <LazyRoute><RaporDetayPage /></LazyRoute>,
      },
      // Redirect /app to /app/araba
      {
        index: true,
        element: <Navigate to="araba" replace />,
      },
    ],
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute>
        <LazyRoute><AdminDashboard /></LazyRoute>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/demo',
    element: <LazyRoute><DemoPage /></LazyRoute>,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/y',
    element: <LazyRoute><InstagramBridgePage /></LazyRoute>,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/a',
    element: <LazyRoute><AppCampaignRedirectPage /></LazyRoute>,
    errorElement: <RouteErrorPage />,
  },
  // Catch all - redirect to home
  {
    path: '*',
    element: <Navigate to="/" replace />,
  }
]);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <IOSAppBanner />
        <RouterProvider router={router} />
        <CookieConsentBanner />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
